import { onSnapshot, Query, DocumentData, DocumentReference, SnapshotOptions } from 'firebase/firestore';
import { Observable } from 'rxjs';

/**
 * Custom implementation of collectionData using Firebase Firestore native API.
 * @param query - Firestore Query reference.
 * @param idField - Optional: Field name to include document ID.
 */
export function collectionData<T = DocumentData>(
  query: Query<T>,
  options?: { idField?: string }
): Observable<T[]> {
  return new Observable<T[]>((observer) => {
    const unsubscribe = onSnapshot(
      query,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => {
          const docData = doc.data();
          if (options?.idField) {
            return { ...docData, [options.idField]: doc.id } as T;
          }
          return docData as T;
        });
        observer.next(data);
      },
      (error) => observer.error(error)
    );

    // Cleanup subscription
    return { unsubscribe };
  });
}

/**
 * Custom implementation of docData using Firebase Firestore native API.
 * @param ref - Firestore DocumentReference.
 * @param options - Optional: Snapshot options and idField.
 */
export function docData<T = DocumentData, R extends T = T>(
  ref: DocumentReference<T>,
  options?: {
    idField?: keyof R;
  } & SnapshotOptions
): Observable<T | R | undefined> {
  return new Observable<T | R | undefined>((observer) => {
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data(options) as T;
          if (options?.idField) {
            const result = { ...data, [options.idField]: snapshot.id } as R;
            observer.next(result);
          } else {
            observer.next(data);
          }
        } else {
          observer.next(undefined); // Document does not exist
        }
      },
      (error) => observer.error(error)
    );

    // Cleanup subscription
    return { unsubscribe };
  });
}
